import { definedMap } from "@glideapps/ts-necessities";

import {
    asString,
    loadedDefinedMapWithDefault,
    asArrayValue,
    asBoolean,
    loadedDefinedMap,
    isLoadedDefined,
    isValueNotEmpty,
} from "common/components/component-helpers";
import { LOADING, asAction, ActionSource } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { mapFilterUndefined } from "common/Support";
import SimpleTableView from "../ui/views/simple-table/simple-table-view";
import { makeOnClickFromAction } from "../ui/deprecated/components";
import { SimpleTableRowStyle } from "common/generator/components/simple-table-types";
import { useForceValueProducers } from "hooks/use-value-producers";
import { useForce } from "hooks/use-force";
import type { OnClickHandler } from "hooks/use-on-click-from-action";
import { getIsActionEnabled } from "hooks/use-on-click-from-action";
import { useEditComponentEnabledViaNetworkStatus } from "hooks/use-edit-components-enabled-via-network-status";

export const SimpleTableMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[captionPV, footerPV, rowsPV]] = useForceValueProducers(getValueProducers, force, ctx);
    const [editingAllowed, onLine] = useEditComponentEnabledViaNetworkStatus(env);

    if (rowsPV === LOADING) return env.renderProgress();

    const title = loadedDefinedMapWithDefault(captionPV, "", asString);
    const footer = loadedDefinedMapWithDefault(footerPV, "", asString);
    let allRowsAreLoading = true;

    const rows = mapFilterUndefined(definedMap(rowsPV, asArrayValue) ?? [], rowVP => {
        const inspector = rowVP.getObjectInspector(force);
        if (!isLoadedDefined(inspector)) return undefined;

        const value = definedMap(inspector.getProperty("value"), force);
        // We don't render rows without values, just like we wouldn't
        // render Basic Texts without values.  We do render rows
        // whose values are loading as empty, to avoid the jank once
        // they show up.
        if (isValueNotEmpty(value) === false) return undefined;

        if (value !== LOADING) {
            allRowsAreLoading = false;
        }

        const actionVP = inspector.getProperty("action");

        let action: OnClickHandler | undefined;
        let target: string | undefined;

        if (actionVP !== undefined) {
            const actionReal = loadedDefinedMap(force(actionVP), asAction);
            const isEnabled = getIsActionEnabled(ctx, env, force, editingAllowed, onLine, actionReal);
            if (isEnabled) {
                const { onClick, targetURL } = makeOnClickFromAction(actionReal, ActionSource.None, ctx, env);
                target = targetURL;
                action = onClick;
            }
        }

        const style = asString(definedMap(inspector.getProperty("style"), force)) as SimpleTableRowStyle;

        return {
            label: asString(definedMap(inspector.getProperty("label"), force)),
            value: style === SimpleTableRowStyle.Checkbox ? asBoolean(value) : asString(value),
            style,
            action,
            targetURL: target,
        };
    });

    if (rows.length === 0) return null;
    if (allRowsAreLoading) return env.renderProgress();

    return <SimpleTableView title={title} footer={footer} rows={rows} />;
};
